import { FormLabel, InputGroup, Loading } from '@/components'
import { QuoteItemDiscountRequestForm } from '@/components/QuoteItem/QuoteItemDiscountRequestForm'
import { HelperTooltip } from '@/components/ui/HelperTooltip'
import { useAuth } from '@/context/AuthContext'
import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { useGetOfferAvailableDiscounts, useSaveOfferDiscounts } from '@/hooks/useOffer'
import { Box, Button, Flex, FormControl, Grid, InputRightAddon, Text } from '@chakra-ui/react'
import type { DiscountRequest, Offer, OfferDiscounts } from 'BrokerTsLib/src/dtos'
import { DiscountType } from 'BrokerTsLib/src/dtos'
import { DiscountRequestState, Insurer } from 'BrokerTsLib/src/enums'
import * as global from 'brokertslib/src/helpers/global'
import { yupResolver } from 'BrokerTsLib/src/rhf'
import React, { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import * as yup from 'yup'

const schema = yup.object().shape(
  {
    'discounts': yup.array().of(
      yup.object().shape({
        'discount': yup.number()
          .required('Zadejde výši slevy')
          .when(['discount', 'minDiscount', 'maxDiscount'], ((discount?: number, min?: number, max?: number, schema?: yup.NumberSchema) => {
            if (min) schema = schema?.min(min, 'Sleva není v povoleném rozmezí')
            if (max) schema = schema?.max(max, 'Sleva není v povoleném rozmezí')

            return schema
          }) as any)
      }, [
        // osetreni cyclic dependency vznikajici pri pouziti when... is... then...
        ['discount', 'discount']
      ])
    ),
  }
)

export const OfferPackageSettingsFormDiscounts = ({
  offer,
  isReadOnly,
  submitCallback
}: {
  offer: Offer
  isReadOnly: boolean,
  submitCallback?: () => void
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [requestDiscountData, setRequestDiscountData] =
    useState<DiscountRequest>({
      state: DiscountRequestState.None,
      userCanApprove: false,
      discounts: [],
    })

  const { user } = useAuth()
  const allowDiscountsOverLimit = user?.allowDiscountsOverLimit ?? false

  const formMethods = useForm<OfferDiscounts>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      discounts: offer.discounts
    }
  })

  const {
    handleSubmit,
    setError,
    watch,
    reset
  } = formMethods

  const { data: discountsData, isLoading } = useGetOfferAvailableDiscounts(offer.offerId)

  useEffect(() => {
    if (!discountsData?.data) return

    // po kazdem prenacteni slev se musi resetovat formular.
    reset({
      discounts: discountsData.data.availableDiscounts?.filter(x => x.editK2 || x.showK2) ?? [],
    })

    if (allowDiscountsOverLimit && discountsData.data.discountRequest) {
      setRequestDiscountData(discountsData.data.discountRequest)
    }
  }, [discountsData])

  const discounts = watch('discounts')
  const filteredDiscounts = discounts?.map((x, i) => { return { x, i } }) ?? []
  const anyDiscounts = filteredDiscounts.length > 0

  const { isDisabled } = useInsuranceParams()
  const { mutateAsync: saveDiscounts } = useSaveOfferDiscounts()

  const submitForm = handleSubmit((data) => {
    setIsSubmitting(true)
    saveDiscounts({ data, offerId: offer.offerId })
      .then(() => {
        submitCallback?.()
      })
      .catch((err) => {
        if (!(err.response && err.response.status === 400 && err.response.data))
          return

        const data = err.response.data
        // zobrazeni serverove validace
        Object.keys(data).forEach((x: any) => {
          setError(global.toCamelCase(x), { message: data[x].join(' ') })
        })
      })
      .finally(() => {
        setIsSubmitting(false)
      })
  })

  // TODO: sjednostit s QuoteItemDiscountsForm - formular je cca stejny
  return (
    <>
      <FormProvider {...formMethods}>
        <form onSubmit={submitForm}>
          <Box sx={{ height: '100%' }}>
            <Grid
              sx={{
                height: '100%',
                gridTemplateRows: '1fr auto',
                rowGap: 4
              }}
            >
              {isLoading && <Loading large />}
              {!isLoading &&

                <Grid
                  sx={{
                    height: '100%',
                    width: '100%',
                    gridTemplateColumns: { lg: 'repeat(2, 1fr)' },
                    // paddingLeft: 8,
                    columnGap: 8,
                    rowGap: 6,
                  }}
                >
                  {!anyDiscounts && <Text>Žádné slevy nejsou k dispozici</Text>}

                  {anyDiscounts &&
                    <>
                      {filteredDiscounts.map(disc => {
                        const discError = formMethods.getFieldState(`discounts.${disc.i}.discount`)?.error
                        const voucherError = formMethods.getFieldState(`discounts.${disc.i}.voucher`)?.error
                        const isKoopVoucher = offer.insurerId == Insurer.Kooperativa && disc.x.type == DiscountType.Voucher

                        return (
                          <React.Fragment key={disc.x.insuranceCategoryDiscountId}>
                            <FormControl isInvalid={!!discError}>
                              <Flex sx={{ width: '100%', gap: 4, justifyContent: 'space-between' }}>
                                <FormLabel htmlFor={`discounts.${disc.i}.insuranceCategoryDiscountId`}>
                                  {disc.x.name}&nbsp;<Text>(max. {disc.x.maxDiscount} {disc.x.unit})</Text>

                                  {disc.x.description && <HelperTooltip label={disc.x.description} />}
                                </FormLabel>
                              </Flex>

                              <InputGroup
                                htmlSize={12}
                                type='number'
                                variant='outline'
                                errors={discError}
                                {...formMethods.register(`discounts.${disc.i}.discount`)}
                                sx={{ fontSize: 'md', fontWeight: 'bold' }}
                                isDisabled={isDisabled || !disc.x.editK2}
                                rightElement={<InputRightAddon>{disc.x.unit}</InputRightAddon>}
                              >
                              </InputGroup>

                            </FormControl>

                            {isKoopVoucher &&
                              <FormControl isInvalid={!!voucherError}>
                                <Flex sx={{ width: '100%', gap: 4, justifyContent: 'space-between' }}>
                                  <FormLabel>
                                    Kód voucheru
                                  </FormLabel>
                                </Flex>

                                <InputGroup
                                  variant='outline'
                                  {...formMethods.register(`discounts.${disc.i}.voucher`)}
                                  errors={voucherError}
                                  sx={{ fontSize: 'md', fontWeight: 'bold' }}
                                  isDisabled={isDisabled}
                                >
                                </InputGroup>
                              </FormControl>
                            }
                          </React.Fragment>
                        )
                      })}
                    </>
                  }

                </Grid>
              }

              {allowDiscountsOverLimit &&
                <QuoteItemDiscountRequestForm offerId={offer.offerId} requestData={requestDiscountData} />
              }

            </Grid>
          </Box>

          {!isReadOnly &&
            <Button
              ml='auto'
              isDisabled={isLoading}
              isLoading={isLoading || isSubmitting}
              colorScheme="green"
              onClick={submitForm}>
              Uložit
            </Button>
          }
        </form>
      </FormProvider>
    </>
  )
}